import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE, PAGE_SIZE } from "@shared/utils/constants";

import clsx from "clsx";
import { usePatchDeviceManagement, usePremise, useRoute } from "@features/mx/device-management/hooks";
import { useToast } from "@shared/hooks/use-toast";
import { useParams } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";

interface UpdatePremiseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
   meter: any;
}

export const UpdatePremiseModal: React.FC<UpdatePremiseModalProps> = ({
  open,
  onOpenChange,
  meter
}) => {
  const [selectedPremise, setSelectedPremise] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
   const { id } = useParams();
const { toast } = useToast();
const patchDeviceMutation = usePatchDeviceManagement(id);
  // Fetch premise data
  const excludeCodeName = meter?.premise ;
  const { data: premiseData } = usePremise( currentPage,MODAL_PAGE_SIZE,searchTerm,excludeCodeName );



  const {data:routeData}=useRoute(selectedPremise)

 const firstRouteName = routeData?.results?.[0]?.routeName ;
 const readCycleName=routeData?.results?.[0]?.meterCycleName; 
  // Transform data for table
  const consumerData = (premiseData?.result || []).map((item) => ({
    id: item.hierarchyCodes?.premise,
    name: item.name || "NA",
    area: item.hierarchyNames?.area || "NA",
    subArea: item.hierarchyNames?.subArea || "NA",
    meters: item.installedMeterCount ?? "NA",
    consumers: item.billedUnitCount ?? "NA",
  }));

  // Get selected premise data
  const selectedPremiseData = consumerData.find(
    (route) => route.id === selectedPremise
  );

  // Pagination state
  const pagination = useMemo(() => {
    const totalItems = premiseData?.count || consumerData.length;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    };
  }, [premiseData?.count, consumerData.length, currentPage]);

  // Handle premise selection (single selection)
  const handleSelectPremise = (id: string) => {
    setSelectedPremise((prev) => (prev === id ? null : id));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };

  // Define table columns
  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Premise",
        headerTooltip:
          "Building or location assigned to this route. Click header to sort.",
        sortable: true,
      },
      {
        key: "area",
        header: "Area",
        headerTooltip:
          "Geographic zone for route planning. Filter to focus on specific regions.",
        sortable: true,
      },
      {
        key: "subArea",
        header: "Subarea",
        headerTooltip:
          "Subdivision within area for granular workload distribution.",
        sortable: true,
      },
      {
        key: "meters",
        header: "Meters",
        headerTooltip:
          "Number of meters at this premise. Higher counts indicate larger workload.",
        sortable: true,
      },
      {
        key: "consumers",
        header: "Consumers",
        headerTooltip:
          "Number of service accounts at this premise. Used for billing and customer service.",
        sortable: true,
      },
      {
        key: "action",
        header: "Action",
        width: "w-24",
        sortable: false,
        render: (_, row) => (
          <Button
            variant={selectedPremise === row.id ? "default" : "outline"}
            onClick={() => handleSelectPremise(row.id)}
          >
            Select
          </Button>
        ),
      },
    ],
    [selectedPremise]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Update Premise, Route & Read Cycle
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Review current assignments. Search and select a new premise to preview changes before updating.
          </DialogDescription>
        </DialogHeader>

        {/* --- Current Info --- */}
        <div className="grid grid-cols-3 gap-4 my-6">
          {[
            { title: "Current Premise", content: meter?.premiseName||"NA" },
            { title: "Current Route", content: meter?.route||"NA"},
            { title: "Current Read Cycle", content:meter?.meterCycle||"NA" },
          ].map((item, i) => (
            <div key={i} className="border rounded-xl bg-white p-4 space-y-2">
              <div className="text-sm font-medium text-gray-500">{item.title}</div>
              <div className="text-base font-semibold text-gray-900">{item.content}</div>
            </div>
          ))}
        </div>

        {/* --- Premise Selection Table --- */}
        <div className="mb-6">
          <GenericTable
            data={consumerData}
            columns={columns}
            rowKey="id"
            emptyMessage="No premises found matching your criteria"
            pagination={pagination}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by premise name or address"
          />
        </div>

        {/* --- Route & Read Cycle Preview --- */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-sm font-medium text-gray-700">Route (Preview)</label>
            <Input value={firstRouteName}  readOnly disabled  />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Read Cycle (Preview)</label>
            <Input value={readCycleName} readOnly disabled />
          </div>  
        </div>


        {/* --- Preview Changes --- */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-800">Preview Changes</h4>
          <div className="grid grid-cols-3 gap-4">
            {/* Premise */}
            <div className="border rounded-xl bg-white p-4 space-y-2">
              <div className="text-sm font-medium text-gray-500">Premise</div>
              <div className="text-sm text-gray-500">Existing</div>
              <div className="text-base font-semibold text-gray-900">{meter?.premiseName||"NA"}</div>
              <hr/>
              <div className="text-sm text-gray-500">Updated</div>
              <div className="text-base font-semibold text-gray-900">
                {selectedPremiseData?.name || "—"}
              </div>
            </div>

            {/* Route */}
            <div className="border rounded-xl bg-white p-4 space-y-2">
              <div className="text-sm font-medium text-gray-500">Route</div>
              <div className="text-sm text-gray-500">Existing</div>
              <div className="text-base font-semibold text-gray-900">{ meter?.route||"NA"}</div>
               <hr/>
              <div className="text-sm text-gray-500">Updated</div>
              <div className="text-base font-semibold text-gray-900">{firstRouteName}</div>
            </div>

            {/* Read Cycle */}
            <div className="border rounded-xl bg-white p-4 space-y-2">
              <div className="text-sm font-medium text-gray-500">Read Cycle</div>
              <div className="text-sm text-gray-500">Existing</div>
              <div className="text-base font-semibold text-gray-900">{meter?.meterCycle||"NA"}</div>
               <hr/>
              <div className="text-sm text-gray-500">Updated</div>
              <div className="text-base font-semibold text-gray-900">{readCycleName}</div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-8 flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        <Button 
  className="bg-blue-600 hover:bg-blue-700 text-white"
  disabled={!selectedPremise || patchDeviceMutation.isPending}
  onClick={() => {
    if (selectedPremise) {
      patchDeviceMutation.mutate(
        {
          deviceId: meter?.id,
          payload: { premise: selectedPremise }
        },
        {
         onSuccess: () => {
  toast({
    title: "Success",
    description: "Premise updated successfully",
  });
  
  queryClient.invalidateQueries({
    queryKey: ['mx', 'device', Number(id)]
  });
  
  onOpenChange(false);
},
          onError: (error) => {
            toast({
              title: "Error",
              description: "Failed to update premise. Please try again.",
              variant: "destructive",
            });
          }
        }
      );
    }
  }}
>
  {patchDeviceMutation.isPending ? "Updating..." : "Update"}
</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};